import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

export default function Accessibility() {
  return (
    <div data-testid="accessibility-page">
      <section className="bg-[var(--rts-blue-pale)] py-20">
        <div className="max-w-4xl mx-auto px-6">
          <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-4">
            ADA Compliance
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--rts-ink)] leading-[1.05]">
            Website Accessibility Statement
          </h1>
          <p className="mt-5 text-sm text-[var(--rts-muted)]">Last Updated: February 27, 2026</p>
        </div>
      </section>

      <article className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-6 prose-style text-[var(--rts-ink-soft)] leading-relaxed space-y-10">
          <Section title="Our Commitment to Accessibility">
            <p>
              We are committed to ensuring digital accessibility for people with disabilities. We are continually improving the user experience for everyone and applying relevant accessibility standards to enhance usability and inclusivity. Our goal is to provide a website that is accessible to the widest possible audience, regardless of technology or ability.
            </p>
          </Section>

          <Section title="Accessibility Standards">
            <p>
              We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA, which are widely recognized as the industry standard for website accessibility. Accessibility is an ongoing process, and we are actively working toward improving the accessibility and usability of our website.
            </p>
          </Section>

          <Section title="Accessibility Technology & Tools">
            <p>
              To support our accessibility efforts, we have deployed industry-standard accessibility technology designed to enhance usability and assist visitors with disabilities. These tools may provide features such as:
            </p>
            <ul className="list-disc ml-6 mt-3 space-y-1.5">
              <li>Screen reader enhancements</li>
              <li>Keyboard navigation improvements</li>
              <li>Color contrast adjustments</li>
              <li>Text size and spacing controls</li>
              <li>Focus indicators</li>
              <li>Additional accessibility interface options</li>
            </ul>
            <p className="mt-3">
              This technology helps us improve accessibility while we continue broader auditing, remediation, and development efforts.
            </p>
          </Section>

          <Section title="Ongoing Improvement">
            <p>Accessibility is an ongoing initiative. We are:</p>
            <ul className="list-disc ml-6 mt-3 space-y-1.5">
              <li>Reviewing site content and functionality regularly</li>
              <li>Working with developers and accessibility tools</li>
              <li>Identifying and remediating barriers</li>
              <li>Incorporating accessibility into future design and development</li>
            </ul>
            <p className="mt-3">
              We understand accessibility is a continuous journey and are committed to making improvements over time.
            </p>
          </Section>

          <Section title="Third-Party Content">
            <p>
              Some areas of the website may include third-party content, plugins, or integrations that are not fully controlled by us. While we encourage vendors to provide accessible content, we cannot guarantee accessibility of third-party features. However, we welcome feedback regarding any issues encountered.
            </p>
          </Section>

          <Section title="Feedback & Assistance">
            <p>
              If you experience difficulty accessing any part of this website, need assistance, or would like to request content in an alternative format, please contact us.
            </p>
            <p className="mt-3">Please include:</p>
            <ul className="list-disc ml-6 mt-2 space-y-1.5">
              <li>The page URL</li>
              <li>Description of the issue</li>
              <li>Your preferred contact method</li>
            </ul>
            <div className="mt-5 p-5 rounded-xl bg-[var(--rts-blue-pale)] border-l-4 border-[var(--rts-blue)] flex items-start gap-3">
              <Mail className="w-5 h-5 text-[var(--rts-blue-deep)] mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-[var(--rts-ink)]">Contact</p>
                <p>Information on our <Link to="/connect-with-us" className="underline text-[var(--rts-blue-deep)] font-semibold">Contact Us page</Link>.</p>
              </div>
            </div>
            <p className="mt-3">
              We aim to respond within a reasonable timeframe and will make reasonable efforts to provide accessible alternatives.
            </p>
          </Section>

          <Section title="Good Faith Efforts">
            <p>
              We are making good-faith efforts to comply with accessibility standards and improve usability for individuals with disabilities. Despite our efforts, some content may not yet fully meet the strictest accessibility standards due to the evolving nature of technology and ongoing updates. We appreciate your understanding as we continue improving accessibility.
            </p>
          </Section>

          <Section title="Terms & Conditions - Accessibility Clause">
            <p>
              We are committed to providing an accessible website experience for all users and strive to follow recognized accessibility standards, including WCAG guidelines. We have implemented industry-standard accessibility tools and continue to review, update, and improve our website to enhance accessibility. Because accessibility is an ongoing process, some areas of the website may not yet fully conform to all accessibility standards. If you encounter accessibility barriers, please contact us so we can provide assistance, offer alternative access, and address the issue where feasible. Nothing in these Terms is intended to limit any rights available under applicable accessibility laws.
            </p>
          </Section>
        </div>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-3xl text-[var(--rts-ink)] mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
